/**
 * Display helpers of the Installments container (C33). Labels only: every figure is the server's (preview `derived`,
 * `schedule`, the read model); the few display-only operations (profit above cost, progress %) work on decimal strings
 * as the C22 helpers do (ADR-02 §2.9 — no `number` for a posted amount).
 */
import { whatsappPhone } from "@faylasuf/contracts";
import { formatWithCommas } from "@faylasuf/ui";
import type { Translate } from "@faylasuf/i18n";
import { decSub, toScaled } from "../customers/format.js";

/** "1,250,000 IQD" — the old `${formatNumber(x)} ${currency}` (IV passim). */
export function money(v: string | null | undefined, cur: string): string {
  return `${formatWithCommas(trimZeros(v ?? "0"))} ${cur}`;
}

export function trimZeros(v: string): string {
  return v.includes(".") ? v.replace(/0+$/, "").replace(/\.$/, "") : v;
}

/** Half-up to a whole number (a price pre-fill from an exact average cost, D-292). */
export function roundWhole(v: string): string {
  const neg = v.startsWith("-");
  const [i = "0", f = ""] = v.replace(/^-/, "").split(".");
  let n = BigInt(i || "0");
  if ((f[0] ?? "0") >= "5") n += 1n;
  return `${neg && n !== 0n ? "-" : ""}${n.toString()}`;
}

export const profitAbove = (profit: string, percentage: string): string => decSub(profit, percentage);

/** Percentage a / b to one decimal (display only), "" when b is 0. */
export function pctOf(a: string, b: string): string {
  const bb = toScaled(b);
  if (bb === 0n) return "";
  const x = (toScaled(a) * 1000n) / bb; // tenths of a percent
  const neg = x < 0n;
  const ab = neg ? -x : x;
  return `${neg ? "-" : ""}${(ab / 10n).toString()}.${(ab % 10n).toString()}`;
}

/** Progress of a contract, whole percent 0 – 100 (old `progressPct`, IV:2772). */
export function progress(paid: string, total: string): number {
  const tt = toScaled(total);
  if (tt <= 0n) return 0;
  const x = (toScaled(paid) * 100n) / tt;
  return Number(x < 0n ? 0n : x > 100n ? 100n : x);
}

export function periodWord(t: Translate, period: string): string {
  return period === "daily" ? t("in.period_daily") : period === "weekly" ? t("in.period_weekly") : t("in.period_monthly");
}

/** Office date of an instant (D-064, D-162). */
export function officeDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}

/** Days between two office dates (b − a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** [C35] D-150: the stored phone in international form (07… → 9647…, F-221). */
export function whatsappUrl(phone: string | null, text: string): string {
  const digits = whatsappPhone(phone) ?? "";
  return `https://api.whatsapp.com/send?${digits ? `phone=${digits}&` : ""}text=${encodeURIComponent(text)}`;
}
